// useAladinnData.js - Version complète avec gestion des synthèses d'étapes et des notes par étape
import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import sourceService from '../CP-Sourcing/SourceService';

// Configuration globale
export const CHAT_CONFIG = {
  maxMessages: 100,
  autoSaveInterval: 15000,
  contextUpdateThreshold: 3,
  maxContextLength: 2000,
  maxSources: 20,
  debounceDelay: 5000,
  maxRetries: 3,
  sourceCacheTime: 300000 // 5 minutes
};

// Utilitaire de debouncing optimisé
export const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

// Cache intelligent pour les sources
const sourcesCache = new Map();

const cacheManager = {
  getCacheKey: (userId, competenceId) => `${userId}_${competenceId}`,
  
  get: (userId, competenceId) => {
    const key = cacheManager.getCacheKey(userId, competenceId);
    const cached = sourcesCache.get(key);
    
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > CHAT_CONFIG.sourceCacheTime) {
      sourcesCache.delete(key);
      return null;
    }
    
    return cached.data;
  },
  
  set: (userId, competenceId, data) => {
    const key = cacheManager.getCacheKey(userId, competenceId);
    sourcesCache.set(key, {
      data,
      timestamp: Date.now()
    });
  },
  
  clear: (userId, competenceId) => {
    if (userId && competenceId) {
      const key = cacheManager.getCacheKey(userId, competenceId);
      sourcesCache.delete(key);
    } else {
      sourcesCache.clear();
    }
  }
};

// Hook principal pour la gestion des données avec étape courante, synthèses et notes par étape
export const useAladinnData = (competenceId) => {
  // États principaux
  const [competenceData, setCompetenceData] = useState(null);
  const [userObjective, setUserObjective] = useState('');
  const [searchObjective, setSearchObjective] = useState('');
  const [savedSources, setSavedSources] = useState([]);
  const [actionPlan, setActionPlan] = useState(null);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  
  // Références pour éviter les boucles
  const unsubscribeRef = useRef(null);
  const isInitializedRef = useRef(false);
  const lastSourcesFetchRef = useRef(0);
  const isFetchingSourcesRef = useRef(false);
  const lastDataHashRef = useRef('');
  const isMountedRef = useRef(true);
  
  // Refs d'état pour comparaisons stables
  const savedSourcesRef = useRef([]);
  const userObjectiveRef = useRef('');
  const searchObjectiveRef = useRef('');
  const competenceDataRef = useRef(null);
  const actionPlanRef = useRef(null);
  const currentUserRef = useRef(null);
  const currentStepIdRef = useRef(null);
  
  // Mettre à jour les refs quand les états changent
  useEffect(() => { 
    if (isMountedRef.current) {
      savedSourcesRef.current = savedSources; 
    }
  }, [savedSources]);
  
  useEffect(() => { 
    if (isMountedRef.current) {
      userObjectiveRef.current = userObjective; 
    }
  }, [userObjective]);
  
  useEffect(() => { 
    if (isMountedRef.current) {
      searchObjectiveRef.current = searchObjective; 
    }
  }, [searchObjective]);
  
  useEffect(() => { 
    if (isMountedRef.current) {
      competenceDataRef.current = competenceData; 
    }
  }, [competenceData]);

  useEffect(() => { 
    if (isMountedRef.current) {
      actionPlanRef.current = actionPlan; 
    }
  }, [actionPlan]);

  useEffect(() => { 
    if (isMountedRef.current) {
      currentStepIdRef.current = currentStepId; 
    }
  }, [currentStepId]);

  // Initialiser currentUserRef
  useEffect(() => {
    currentUserRef.current = auth.currentUser;
  }, []);

  // Fonction pour créer un hash des données importantes
  const createDataHash = useCallback((data) => {
    const relevantData = {
      title: data?.title,
      searchObjective: data?.searchObjective,
      inspirationData: data?.inspirationData?.searchQuery,
      conversationData: data?.conversationData?.searchObjective,
      actionPlan: data?.actionPlan,
      weeklyPlanning: data?.weeklyPlanning,
      currentStepId: data?.currentStepId,
      stepSyntheses: data?.stepSyntheses,
      stepNotes: data?.stepNotes,
      userNotes: data?.userNotes
    };
    return JSON.stringify(relevantData);
  }, []);

  // Fonction pour calculer l'étape courante par défaut
  const calculateDefaultCurrentStep = useCallback((plan) => {
    if (!plan?.steps || plan.steps.length === 0) {
      return null;
    }

    const firstIncomplete = plan.steps.find(step => !step.termine);
    if (firstIncomplete) {
      return firstIncomplete.id;
    }

    const lastStep = plan.steps[plan.steps.length - 1];
    return lastStep?.id || null;
  }, []);

  // Fonction pour changer d'étape avec sauvegarde Firebase
  const changeCurrentStep = useCallback(async (stepId) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id) {
      console.warn('Données manquantes pour changer d\'étape');
      return false;
    }

    const currentActionPlan = actionPlanRef.current;
    if (stepId && currentActionPlan?.steps && !currentActionPlan.steps.find(s => s.id === stepId)) {
      console.error('Étape non trouvée:', stepId);
      return false;
    }

    try {
      console.log('Changement d\'étape courante vers:', stepId);
      
      setCurrentStepId(stepId);
      currentStepIdRef.current = stepId;

      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      await updateDoc(docRef, {
        currentStepId: stepId,
        currentStepUpdatedAt: serverTimestamp(),
        lastStepChangeAt: new Date().toISOString()
      });

      console.log('Étape courante sauvegardée:', stepId);
      return true;
      
    } catch (error) {
      console.error('Erreur sauvegarde étape courante:', error);
      
      const previousStepId = currentStepIdRef.current;
      setCurrentStepId(previousStepId);
      currentStepIdRef.current = previousStepId;
      
      return false;
    }
  }, []);

  // Fonction pour obtenir les détails de l'étape courante
  const getCurrentStep = useCallback(() => {
    const currentActionPlan = actionPlanRef.current;
    const currentStepIdValue = currentStepIdRef.current;
    
    if (!currentActionPlan?.steps || !currentStepIdValue) {
      return null;
    }

    return currentActionPlan.steps.find(step => step.id === currentStepIdValue) || null;
  }, []);

  // ===== GESTION DES SYNTHÈSES D'ÉTAPES =====

  const saveStepSynthesis = useCallback(async (stepId, synthesis) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId || !synthesis) {
      console.error('Données manquantes pour sauvegarder la synthèse');
      return false;
    }

    try {
      console.log('Sauvegarde synthèse d\'étape:', stepId);
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentSyntheses = currentData.stepSyntheses || {};
        
        const updatedSyntheses = {
          ...currentSyntheses,
          [stepId]: {
            ...synthesis,
            updatedAt: serverTimestamp(),
            stepId
          }
        };
        
        await updateDoc(docRef, {
          stepSyntheses: updatedSyntheses,
          stepSynthesesUpdatedAt: serverTimestamp()
        });
        
        setCompetenceData(prev => ({
          ...prev,
          stepSyntheses: updatedSyntheses
        }));
        
        console.log('Synthèse d\'étape sauvegardée avec succès');
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur sauvegarde synthèse d\'étape:', error);
      return false;
    }
  }, []);

  const getStepSynthesis = useCallback((stepId) => {
    const currentCompetenceData = competenceDataRef.current;
    return currentCompetenceData?.stepSyntheses?.[stepId] || null;
  }, []);

  const deleteStepSynthesis = useCallback(async (stepId) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId) {
      return false;
    }

    try {
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentSyntheses = { ...currentData.stepSyntheses };
        delete currentSyntheses[stepId];
        
        await updateDoc(docRef, {
          stepSyntheses: currentSyntheses,
          stepSynthesesUpdatedAt: serverTimestamp()
        });
        
        setCompetenceData(prev => ({
          ...prev,
          stepSyntheses: currentSyntheses
        }));
        
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur suppression synthèse:', error);
      return false;
    }
  }, []);

  // ===== GESTION DES NOTES PAR ÉTAPE =====

  /**
   * Sauvegarde les notes d'une étape spécifique
   */
  const saveStepNotes = useCallback(async (stepId, stepNotesData) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId) {
      console.error('Données manquantes pour sauvegarder les notes d\'étape');
      return false;
    }

    try {
      console.log('Sauvegarde notes d\'étape:', stepId);
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentStepNotes = currentData.stepNotes || {};
        
        const updatedStepNotes = {
          ...currentStepNotes,
          [stepId]: {
            ...stepNotesData,
            stepId,
            updatedAt: new Date().toISOString(),
            lastModified: serverTimestamp()
          }
        };
        
        await updateDoc(docRef, {
          stepNotes: updatedStepNotes,
          stepNotesUpdatedAt: serverTimestamp()
        });
        
        // Mettre à jour l'état local
        setCompetenceData(prev => ({
          ...prev,
          stepNotes: updatedStepNotes
        }));
        
        console.log('Notes d\'étape sauvegardées avec succès');
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur sauvegarde notes d\'étape:', error);
      return false;
    }
  }, []);

  /**
   * Met à jour une section spécifique des notes d'étape
   */
  const updateStepNoteSection = useCallback(async (stepId, sectionId, content) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId || !sectionId) {
      console.error('Données manquantes pour mettre à jour la section');
      return false;
    }

    try {
      console.log('Mise à jour section:', stepId, sectionId);
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentStepNotes = currentData.stepNotes || {};
        const currentStepData = currentStepNotes[stepId] || {};
        
        const updatedStepData = {
          ...currentStepData,
          [sectionId]: content,
          stepId,
          lastSectionUpdate: sectionId,
          updatedAt: new Date().toISOString()
        };
        
        const updatedStepNotes = {
          ...currentStepNotes,
          [stepId]: updatedStepData
        };
        
        await updateDoc(docRef, {
          stepNotes: updatedStepNotes,
          stepNotesUpdatedAt: serverTimestamp()
        });
        
        // Mettre à jour l'état local
        setCompetenceData(prev => ({
          ...prev,
          stepNotes: updatedStepNotes
        }));
        
        console.log('Section mise à jour avec succès');
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur mise à jour section:', error);
      return false;
    }
  }, []);

  /**
   * Récupère les notes d'une étape spécifique
   */
  const getStepNotes = useCallback((stepId) => {
    const currentCompetenceData = competenceDataRef.current;
    
    if (!stepId || !currentCompetenceData?.stepNotes) {
      return {};
    }
    
    return currentCompetenceData.stepNotes[stepId] || {};
  }, []);

  /**
   * Supprime les notes d'une étape
   */
  const deleteStepNotes = useCallback(async (stepId) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId) {
      return false;
    }

    try {
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentStepNotes = { ...currentData.stepNotes };
        delete currentStepNotes[stepId];
        
        await updateDoc(docRef, {
          stepNotes: currentStepNotes,
          stepNotesUpdatedAt: serverTimestamp()
        });
        
        // Mettre à jour l'état local
        setCompetenceData(prev => ({
          ...prev,
          stepNotes: currentStepNotes
        }));
        
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur suppression notes d\'étape:', error);
      return false;
    }
  }, []);

  /**
   * Obtient les statistiques des notes par étape
   */
  const getStepNotesStats = useCallback(() => {
    const currentCompetenceData = competenceDataRef.current;
    const stepNotes = currentCompetenceData?.stepNotes || {};
    
    const stats = {
      totalSteps: Object.keys(stepNotes).length,
      totalSections: 0,
      completedSections: 0,
      byRideau: {
        decisions: 0,
        challenges: 0, 
        recommendations: 0
      },
      lastUpdated: null
    };
    
    // Sections possibles par rideau
    const rideauSections = {
      decisions: ['decisions', 'orientations', 'actions_done', 'actions_planned'],
      challenges: ['questions', 'difficulties', 'decisions_pending', 'risks_opportunities', 'todo_contacts'],
      recommendations: ['ai_recommendations', 'best_practices', 'resources']
    };
    
    Object.values(stepNotes).forEach(stepData => {
      Object.entries(rideauSections).forEach(([rideauId, sections]) => {
        sections.forEach(sectionId => {
          stats.totalSections++;
          if (stepData[sectionId] && stepData[sectionId].trim().length > 0) {
            stats.completedSections++;
            stats.byRideau[rideauId]++;
          }
        });
      });
      
      if (stepData.updatedAt) {
        const updateDate = new Date(stepData.updatedAt);
        if (!stats.lastUpdated || updateDate > stats.lastUpdated) {
          stats.lastUpdated = updateDate;
        }
      }
    });
    
    stats.completionRate = stats.totalSections > 0 ? 
      Math.round((stats.completedSections / stats.totalSections) * 100) : 0;
    
    return stats;
  }, []);

  /**
   * Copie les notes d'une étape vers une autre
   */
  const copyStepNotes = useCallback(async (sourceStepId, targetStepId) => {
    const sourceNotes = getStepNotes(sourceStepId);
    
    if (!sourceNotes || Object.keys(sourceNotes).length === 0) {
      console.warn('Aucune note à copier depuis l\'étape source');
      return false;
    }
    
    // Préparer les notes copiées (sans les métadonnées)
    const notesToCopy = { ...sourceNotes };
    delete notesToCopy.stepId;
    delete notesToCopy.updatedAt;
    delete notesToCopy.lastModified;
    delete notesToCopy.lastSectionUpdate;
    
    return await saveStepNotes(targetStepId, notesToCopy);
  }, [getStepNotes, saveStepNotes]);

  // ===== GESTION DU PLANNING =====

  const saveWeeklyPlanning = useCallback(async (weeklyPlanningData) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id) {
      console.error('Données utilisateur manquantes pour sauvegarder le planning');
      return false;
    }

    if (!weeklyPlanningData || !Array.isArray(weeklyPlanningData)) {
      console.error('Format de planning invalide');
      return false;
    }

    try {
      console.log('Sauvegarde Firebase du planning:', {
        competenceId: currentCompetenceData.id,
        semaines: weeklyPlanningData.length
      });
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        console.error('Document de compétence introuvable');
        return false;
      }
      
      await updateDoc(docRef, {
        weeklyPlanning: weeklyPlanningData,
        weeklyPlanningUpdatedAt: serverTimestamp(),
        lastPlanningUpdate: new Date().toISOString()
      });

      console.log('Planning sauvegardé avec succès dans Firebase');
      return true;
      
    } catch (error) {
      console.error('Erreur sauvegarde planning Firebase:', error);
      throw new Error(`Erreur Firebase: ${error.message}`);
    }
  }, []);

  const saveElementaryAction = useCallback(async (actionData) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !actionData) {
      console.error('Données manquantes pour sauvegarder l\'action élémentaire');
      return false;
    }

    try {
      console.log('Sauvegarde d\'action élémentaire:', actionData.id);
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentPlanning = currentData.weeklyPlanning || [];
        
        const updatedPlanning = currentPlanning.map(week => {
          if (week.elementaryActions) {
            const actionIndex = week.elementaryActions.findIndex(a => a.id === actionData.id);
            if (actionIndex !== -1) {
              week.elementaryActions[actionIndex] = {
                ...week.elementaryActions[actionIndex],
                ...actionData,
                updatedAt: new Date().toISOString()
              };
            }
          }
          return week;
        });
        
        await updateDoc(docRef, {
          weeklyPlanning: updatedPlanning,
          weeklyPlanningUpdatedAt: serverTimestamp()
        });
        
        console.log('Action élémentaire sauvegardée');
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur sauvegarde action élémentaire:', error);
      return false;
    }
  }, []);

  const deleteElementaryAction = useCallback(async (actionId) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !actionId) {
      console.error('Données manquantes pour supprimer l\'action élémentaire');
      return false;
    }

    try {
      console.log('Suppression d\'action élémentaire:', actionId);
      
      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentPlanning = currentData.weeklyPlanning || [];
        
        const updatedPlanning = currentPlanning.map(week => {
          if (week.elementaryActions) {
            week.elementaryActions = week.elementaryActions.filter(a => a.id !== actionId);
          }
          return week;
        });
        
        await updateDoc(docRef, {
          weeklyPlanning: updatedPlanning,
          weeklyPlanningUpdatedAt: serverTimestamp()
        });
        
        console.log('Action élémentaire supprimée');
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Erreur suppression action élémentaire:', error);
      return false;
    }
  }, []);

  // ===== GESTION DES RÉSULTATS =====

  const saveStepResults = useCallback(async (stepId, newResults) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    const currentActionPlan = actionPlanRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId || !currentActionPlan) {
      console.error('Données manquantes pour sauvegarder les résultats');
      return false;
    }

    try {
      console.log('Sauvegarde des résultats pour l\'étape:', stepId);
      
      const updatedSteps = currentActionPlan.steps.map(step => {
        if (step.id === stepId) {
          const existingResults = step.resultats || [];
          
          const filteredNewResults = Array.isArray(newResults) ? newResults : [newResults];
          const validNewResults = filteredNewResults.filter(newResult => 
            newResult && 
            !existingResults.some(existing => 
              existing.contenu === newResult.contenu && 
              existing.type === newResult.type &&
              Math.abs(new Date(existing.timestamp).getTime() - new Date(newResult.timestamp).getTime()) < 60000
            )
          );
          
          if (validNewResults.length === 0) {
            console.log('Aucun nouveau résultat à ajouter (doublons évités)');
            return step;
          }
          
          return {
            ...step,
            resultats: [...existingResults, ...validNewResults],
            lastResultUpdate: new Date().toISOString(),
            totalResults: (existingResults.length + validNewResults.length),
            totalPoints: [...existingResults, ...validNewResults].reduce((sum, r) => sum + (r.points || 0), 0)
          };
        }
        return step;
      });

      const updatedPlan = {
        ...currentActionPlan,
        steps: updatedSteps,
        lastResultsUpdate: new Date().toISOString()
      };

      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      await updateDoc(docRef, {
        actionPlan: updatedPlan,
        actionPlanUpdatedAt: serverTimestamp()
      });

      setActionPlan(updatedPlan);
      actionPlanRef.current = updatedPlan;
      
      console.log('Résultats sauvegardés avec succès');
      return true;
      
    } catch (error) {
      console.error('Erreur sauvegarde résultats:', error);
      throw new Error('Erreur lors de la sauvegarde des résultats: ' + error.message);
    }
  }, []);

  const updateStepStatus = useCallback(async (stepId, isCompleted) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    const currentCompetenceData = competenceDataRef.current;
    const currentActionPlan = actionPlanRef.current;
    
    if (!currentUser?.uid || !currentCompetenceData?.id || !stepId || !currentActionPlan) {
      return false;
    }

    try {
      console.log(`Mise à jour statut étape ${stepId}:`, isCompleted);
      
      const updatedSteps = currentActionPlan.steps.map(step => 
        step.id === stepId 
          ? { 
              ...step, 
              termine: isCompleted, 
              completedAt: isCompleted ? new Date().toISOString() : null 
            }
          : step
      );

      const updatedPlan = {
        ...currentActionPlan,
        steps: updatedSteps,
        lastStepUpdate: new Date().toISOString()
      };

      let newCurrentStepId = currentStepIdRef.current;
      if (isCompleted && stepId === currentStepIdRef.current) {
        newCurrentStepId = calculateDefaultCurrentStep(updatedPlan);
        console.log('Passage automatique à l\'étape suivante:', newCurrentStepId);
      }

      const docRef = doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id);
      await updateDoc(docRef, {
        actionPlan: updatedPlan,
        currentStepId: newCurrentStepId,
        actionPlanUpdatedAt: serverTimestamp(),
        currentStepUpdatedAt: serverTimestamp()
      });

      setActionPlan(updatedPlan);
      actionPlanRef.current = updatedPlan;
      
      if (newCurrentStepId !== currentStepIdRef.current) {
        setCurrentStepId(newCurrentStepId);
        currentStepIdRef.current = newCurrentStepId;
      }
      
      console.log('Statut étape et étape courante mis à jour');
      return true;
      
    } catch (error) {
      console.error('Erreur mise à jour statut étape:', error);
      return false;
    }
  }, [calculateDefaultCurrentStep]);

  // ===== GESTION DES SOURCES =====

  const fetchSavedSources = useCallback(async (forceRefresh = false) => {
    const currentUser = currentUserRef.current || auth.currentUser;
    
    if (!currentUser?.uid || !competenceId) {
      console.log("Pas d'utilisateur ou de competenceId pour récupérer les sources");
      return [];
    }
    
    if (!forceRefresh) {
      const cachedSources = cacheManager.get(currentUser.uid, competenceId);
      if (cachedSources) {
        console.log("Sources récupérées depuis le cache");
        return cachedSources;
      }
    }
    
    if (isFetchingSourcesRef.current) {
      console.log("Récupération des sources déjà en cours, abandon");
      return savedSourcesRef.current;
    }
    
    const now = Date.now();
    if (!forceRefresh && now - lastSourcesFetchRef.current < CHAT_CONFIG.debounceDelay) {
      const timeLeft = Math.round((CHAT_CONFIG.debounceDelay - (now - lastSourcesFetchRef.current)) / 1000);
      console.log(`Récupération des sources limitée par debounce (${timeLeft}s restantes)`);
      return savedSourcesRef.current;
    }
    
    try {
      isFetchingSourcesRef.current = true;
      lastSourcesFetchRef.current = now;
      
      console.log("Récupération des sources pour le contexte Aladinn...");
      
      const token = await currentUser.getIdToken(false);
      
      const result = await sourceService.getSavedSources(
        currentUser.uid, 
        token, 
        { competenceId }
      );
      
      if (result?.success && Array.isArray(result.sources) && result.sources.length > 0) {
        const latestSession = result.sources
          .sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0))[0];
        
        if (latestSession.sources && Array.isArray(latestSession.sources)) {
          console.log('🔍 DEBUG fetchSavedSources - latestSession:', {
            sessionId: latestSession.sessionId,
            hasSessionId: !!latestSession.sessionId,
            sourcesCount: latestSession.sources.length,
            firstSource: latestSession.sources[0] ? {
              hasSessionId: !!latestSession.sources[0].sessionId,
              sessionId: latestSession.sources[0].sessionId,
              title: latestSession.sources[0].title
            } : null
          });
          
          const formattedSources = latestSession.sources
            .slice(0, CHAT_CONFIG.maxSources)
            .map(source => {
              const sessionId = source.sessionId || latestSession.sessionId;
              console.log('🔍 DEBUG source mapping:', {
                sourceTitle: source.title,
                sourceSessionId: source.sessionId,
                latestSessionId: latestSession.sessionId,
                finalSessionId: sessionId
              });
              
              return {
                title: source.title || source.domain || 'Source sans titre',
                url: source.usableUrl || source.recommendedUrl || source.url || `https://${source.domain}`,
                domain: source.domain || 'Source externe',
                description: source.description || 'Aucune description disponible',
                type: source.type || 'Unknown',
                reliability: source.reliability || 'Medium',
                relevanceScore: source.relevanceScore || 5,
                // Ajouter sessionId pour permettre la suppression des sources
                sessionId: sessionId,
                searchId: sessionId, // alias pour searchId
                createdAt: source.createdAt || latestSession.createdAt,
                updatedAt: source.updatedAt || latestSession.updatedAt
              };
            });
          
          console.log('✅ DEBUG formattedSources avec sessionId:', formattedSources.map(s => ({
            title: s.title,
            sessionId: s.sessionId,
            hasSessionId: !!s.sessionId
          })));
          
          cacheManager.set(currentUser.uid, competenceId, formattedSources);
          
          console.log(`${formattedSources.length} sources récupérées et mises en cache`);
          return formattedSources;
        }
      }
      
      console.log("Aucune source trouvée");
      return [];
      
    } catch (err) {
      console.error('Erreur lors de la récupération des sources:', err);
      return savedSourcesRef.current;
    } finally {
      isFetchingSourcesRef.current = false;
    }
  }, [competenceId]);

  // ===== EFFET PRINCIPAL DE CHARGEMENT =====

  useEffect(() => {
    let isEffectMounted = true;
    
    const initializeData = async () => {
      if (!competenceId) {
        if (isEffectMounted) {
          setError('Aucun identifiant de compétence fourni.');
          setConnectionStatus('error');
          setLoading(false);
        }
        return;
      }

      const currentUser = auth.currentUser;
      if (!currentUser?.uid) {
        if (isEffectMounted) {
          setError('Erreur : Vous devez être connecté pour accéder au chat.');
          setConnectionStatus('error');
          setLoading(false);
        }
        return;
      }

      if (isInitializedRef.current) {
        console.log("Initialisation déjà effectuée, abandon");
        return;
      }

      if (!isEffectMounted) return;

      setLoading(true);
      setConnectionStatus('loading');
      setError('');
      currentUserRef.current = currentUser;

      try {
        console.log("Début de l'initialisation...");
        
        const docRef = doc(db, 'users', currentUser.uid, 'competences', competenceId);
        const docSnap = await getDoc(docRef);
        
        if (!isEffectMounted) return;
        
        if (docSnap.exists()) {
          const data = { ...docSnap.data(), id: competenceId };
          const currentHash = createDataHash(data);
          
          if (!isEffectMounted) return;
          
          setCompetenceData(data);
          competenceDataRef.current = data;
          lastDataHashRef.current = currentHash;
          
          if (data.title) {
            setUserObjective(data.title);
            userObjectiveRef.current = data.title;
          }
          
          const searchObj = data.searchObjective || 
                           data.inspirationData?.searchQuery || 
                           data.conversationData?.searchObjective || '';
          setSearchObjective(searchObj);
          searchObjectiveRef.current = searchObj;
          
          if (data.actionPlan) {
            console.log("Plan d'action trouvé dans les données");
            setActionPlan(data.actionPlan);
            actionPlanRef.current = data.actionPlan;
            
            let stepIdToSet = data.currentStepId;
            
            if (stepIdToSet && data.actionPlan.steps?.find(s => s.id === stepIdToSet)) {
              console.log("Étape courante récupérée depuis Firebase:", stepIdToSet);
            } else {
              stepIdToSet = calculateDefaultCurrentStep(data.actionPlan);
              console.log("Étape courante calculée automatiquement:", stepIdToSet);
              
              if (stepIdToSet && isEffectMounted) {
                setTimeout(async () => {
                  try {
                    await updateDoc(docRef, {
                      currentStepId: stepIdToSet,
                      currentStepUpdatedAt: serverTimestamp()
                    });
                    console.log("Étape par défaut sauvegardée en Firebase");
                  } catch (error) {
                    console.warn("Erreur sauvegarde étape par défaut:", error);
                  }
                }, 1000);
              }
            }
            
            if (stepIdToSet && isEffectMounted) {
              setCurrentStepId(stepIdToSet);
              currentStepIdRef.current = stepIdToSet;
            }
          } else {
            console.log("Aucun plan d'action trouvé");
            setActionPlan(null);
            actionPlanRef.current = null;
            setCurrentStepId(null);
            currentStepIdRef.current = null;
          }
          
          console.log("Récupération initiale des sources...");
          const sources = await fetchSavedSources(true);
          
          if (!isEffectMounted) return;
          
          setSavedSources(sources);
          savedSourcesRef.current = sources;
          setConnectionStatus('connected');
          isInitializedRef.current = true;
          
          console.log("Initialisation terminée avec succès");
          
          // Listener Firebase
          unsubscribeRef.current = onSnapshot(docRef, async (doc) => {
            if (!isEffectMounted || !isMountedRef.current || !doc.exists()) return;
            
            const updatedData = { ...doc.data(), id: competenceId };
            const newHash = createDataHash(updatedData);
            
            if (newHash !== lastDataHashRef.current) {
              console.log("Données Firebase mises à jour");
              
              if (!isEffectMounted || !isMountedRef.current) return;
              
              setCompetenceData(updatedData);
              competenceDataRef.current = updatedData;
              lastDataHashRef.current = newHash;
              
              if (updatedData.title !== userObjectiveRef.current) {
                setUserObjective(updatedData.title || '');
                userObjectiveRef.current = updatedData.title || '';
              }
              
              const updatedSearchObj = updatedData.searchObjective || 
                                     updatedData.inspirationData?.searchQuery || 
                                     updatedData.conversationData?.searchObjective || '';
              
              if (updatedSearchObj !== searchObjectiveRef.current) {
                setSearchObjective(updatedSearchObj);
                searchObjectiveRef.current = updatedSearchObj;
              }
              
              const currentPlanHash = JSON.stringify(actionPlanRef.current);
              const newPlanHash = JSON.stringify(updatedData.actionPlan);
              
              if (currentPlanHash !== newPlanHash) {
                console.log("Plan d'action mis à jour");
                setActionPlan(updatedData.actionPlan || null);
                actionPlanRef.current = updatedData.actionPlan || null;
                
                if (updatedData.actionPlan) {
                  let newCurrentStepId = updatedData.currentStepId;
                  
                  if (!newCurrentStepId || !updatedData.actionPlan.steps?.find(s => s.id === newCurrentStepId)) {
                    newCurrentStepId = calculateDefaultCurrentStep(updatedData.actionPlan);
                    console.log("Recalcul de l'étape courante:", newCurrentStepId);
                  }
                  
                  if (newCurrentStepId !== currentStepIdRef.current) {
                    setCurrentStepId(newCurrentStepId);
                    currentStepIdRef.current = newCurrentStepId;
                  }
                } else {
                  setCurrentStepId(null);
                  currentStepIdRef.current = null;
                }
              } else {
                if (updatedData.currentStepId !== currentStepIdRef.current) {
                  console.log("Étape courante mise à jour depuis Firebase:", updatedData.currentStepId);
                  setCurrentStepId(updatedData.currentStepId);
                  currentStepIdRef.current = updatedData.currentStepId;
                }
              }
              
              console.log("Synchronisation terminée, sources conservées");
            }
          }, (error) => {
            console.error("Erreur listener Firebase:", error);
            if (isEffectMounted && isMountedRef.current) {
              setConnectionStatus('error');
            }
          });
        } else {
          if (isEffectMounted) {
            setError("Cette compétence n'existe pas ou vous n'avez pas les droits pour y accéder.");
            setConnectionStatus('error');
          }
        }
      } catch (err) {
        console.error("Erreur lors de l'initialisation:", err);
        if (isEffectMounted) {
          setError("Une erreur s'est produite lors du chargement. Veuillez réessayer.");
          setConnectionStatus('error');
        }
      } finally {
        if (isEffectMounted) {
          setLoading(false);
        }
      }
    };

    initializeData();

    return () => {
      isEffectMounted = false;
      
      if (unsubscribeRef.current) {
        console.log("Nettoyage du listener Firebase");
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      isInitializedRef.current = false;
      isFetchingSourcesRef.current = false;
    };
  }, [competenceId, createDataHash, fetchSavedSources, calculateDefaultCurrentStep]);

  // Cleanup au démontage du composant
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      console.log("Composant useAladinnData démonté");
    };
  }, []);

  // ===== FONCTIONS UTILITAIRES =====

  const saveChatData = useCallback(async (chatMessages, conversationContext) => {
    const currentCompetenceData = competenceDataRef.current;
    const currentUser = currentUserRef.current || auth.currentUser;
    
    if (!currentCompetenceData?.id || !chatMessages?.length || !currentUser?.uid) {
      return false;
    }

    try {
      const chatData = {
        messages: chatMessages.slice(-CHAT_CONFIG.maxMessages),
        context: conversationContext || '',
        lastUpdate: new Date().toISOString(),
        messageCount: chatMessages.length
      };

      await updateDoc(
        doc(db, 'users', currentUser.uid, 'competences', currentCompetenceData.id),
        {
          chatHistory: chatData,
          updatedAt: serverTimestamp()
        }
      );

      console.log('Chat sauvegardé automatiquement');
      return true;
    } catch (error) {
      console.error('Erreur de sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de la conversation.');
      return false;
    }
  }, []);

  const clearCache = useCallback(() => {
    const currentUser = currentUserRef.current || auth.currentUser;
    if (currentUser && competenceId) {
      cacheManager.clear(currentUser.uid, competenceId);
      console.log("Cache des sources vidé");
    }
  }, [competenceId]);

  const debugDataState = useCallback(() => {
    console.log('DEBUG - État global des données:', {
      competenceId,
      hasCompetenceData: !!competenceData?.id,
      hasActionPlan: !!actionPlan?.steps?.length,
      currentStepId,
      currentStepTitle: getCurrentStep()?.titre,
      hasWeeklyPlanning: !!competenceData?.weeklyPlanning?.length,
      hasStepSyntheses: !!competenceData?.stepSyntheses,
      synthesesCount: Object.keys(competenceData?.stepSyntheses || {}).length,
      hasStepNotes: !!competenceData?.stepNotes,
      stepNotesCount: Object.keys(competenceData?.stepNotes || {}).length,
      stepNotesStats: getStepNotesStats(),
      currentUser: auth.currentUser?.uid,
      connectionStatus,
      loading,
      error: error || 'Aucune erreur'
    });
    
    if (competenceData?.weeklyPlanning) {
      console.log('Planning hebdomadaire actuel:');
      competenceData.weeklyPlanning.forEach((week, index) => {
        console.log(`  Semaine ${index + 1}:`, {
          theme: week.theme,
          actionsCount: week.elementaryActions?.length || 0,
          createdAt: week.createdAt
        });
      });
    }
    
    if (actionPlan?.steps) {
      console.log('Plan d\'action actuel:');
      actionPlan.steps.forEach((step, index) => {
        console.log(`  Étape ${index + 1}:`, {
          titre: step.titre,
          termine: step.termine,
          resultatsCount: step.resultats?.length || 0,
          isCurrent: step.id === currentStepId
        });
      });
    }

    if (competenceData?.stepSyntheses) {
      console.log('Synthèses d\'étapes:');
      Object.entries(competenceData.stepSyntheses).forEach(([stepId, synthesis]) => {
        console.log(`  Étape ${stepId}:`, {
          hasContent: !!synthesis.content,
          isUserModified: synthesis.isUserModified,
          lastModified: synthesis.lastModified
        });
      });
    }

    if (competenceData?.stepNotes) {
      console.log('Notes par étape:');
      Object.entries(competenceData.stepNotes).forEach(([stepId, stepData]) => {
        const sections = Object.keys(stepData).filter(key => 
          !['stepId', 'updatedAt', 'lastModified', 'lastSectionUpdate'].includes(key)
        );
        console.log(`  Étape ${stepId}:`, {
          sectionsCount: sections.length,
          completedSections: sections.filter(key => stepData[key] && stepData[key].trim().length > 0).length,
          lastUpdate: stepData.updatedAt
        });
      });
    }
  }, [competenceData, actionPlan, currentStepId, getCurrentStep, getStepNotesStats, competenceId, connectionStatus, loading, error]);

  // Exposer la fonction de debug (optionnel)
  useEffect(() => {
    window.debugAladinnData = debugDataState;
    return () => delete window.debugAladinnData;
  }, [debugDataState]);

  // ===== RETURN PRINCIPAL =====

  return {
    // États principaux
    competenceData,
    userObjective,
    searchObjective,
    savedSources,
    actionPlan,
    currentStepId,
    loading,
    error,
    connectionStatus,
    
    // Fonctions de base
    saveChatData,
    clearCache,
    
    // Fonctions pour la gestion du planning
    saveWeeklyPlanning,
    saveElementaryAction,
    deleteElementaryAction,
    
    // Fonctions pour la gestion des résultats
    saveStepResults,
    updateStepStatus,
    
    // Fonctions pour l'étape courante
    changeCurrentStep,
    getCurrentStep,
    calculateDefaultCurrentStep,
    
    // Fonctions pour les synthèses d'étapes
    saveStepSynthesis,
    getStepSynthesis,
    deleteStepSynthesis,
    
    // NOUVELLES FONCTIONS pour les notes par étape
    saveStepNotes,
    updateStepNoteSection,
    getStepNotes,
    deleteStepNotes,
    getStepNotesStats,
    copyStepNotes,
    
    // Fonction de debug
    debugDataState
  };
};
